/*
 * Bottom-of-screen morning-meeting nav. Two strips:
 *   - .nav     — the always-visible main strip (Home · Morning meeting · Search)
 *   - .subNav  — the expanded strip that pops above when the teacher
 *                taps "Morning meeting"; lists each individual MM page
 *                (calendar, day, number, weather, feelings, question,
 *                pledges) so they can jump directly.
 *
 * Both are children of .wrapper which docks the whole stack to the
 * bottom-center of the viewport. Wrapper is `position: fixed` (not the
 * .nav) so the expanded strip lifts off the main one without breaking
 * the centering math.
 */

.wrapper {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 4;
  /* Reserve room for the docked corner buttons (back, kiosk, clock) —
   * 56px each — plus a hair of breathing room. */
  max-width: calc(100vw - 240px);
  pointer-events: none;
}

.wrapper > * {
  pointer-events: auto;
}

.nav {
  display: flex;
  gap: 4px;
  padding: 6px 10px 8px;
  border-radius: 24px 24px 0 0;
  background: rgba(255, 255, 255, 0.32);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-bottom: 0;
  box-shadow:
    0 -18px 36px -18px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  font-family: var(--token-font-ui);
  flex-wrap: nowrap;
  justify-content: center;
}

/* Expanded sub-strip sits above the main strip with its own glass
 * material so it reads as a separate, dismissable layer. Pill-shaped
 * (rounded on all four corners) so it visually floats rather than
 * docking. */
.subNav {
  display: flex;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 12px 28px -10px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.65);
  font-family: var(--token-font-ui);
  flex-wrap: nowrap;
  justify-content: center;
  animation: mmSubNavIn 180ms cubic-bezier(0.22, 0.9, 0.3, 1.1);
}

@keyframes mmSubNavIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.button {
  min-height: 36px;
  padding: 4px 14px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--token-text-primary);
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.65),
    0 0 8px rgba(255, 255, 255, 0.4);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.button:hover {
  background: rgba(255, 255, 255, 0.32);
}

.icon {
  display: inline-block;
  margin-right: 4px;
  font-size: 14px;
  line-height: 1;
}

.button[aria-current='page'] {
  background: var(--token-accent);
  color: #fefdf6;
  text-shadow: none;
  box-shadow:
    0 6px 14px -6px rgba(190, 110, 35, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

/* When the morning-meeting tab is open the same orange "current" look
 * applies whether we're on an MM page or not — but we also want a
 * subtle expanded-state cue when the user is NOT on an MM page (e.g.
 * tapping it from Home). The aria-expanded attribute gives us that. */
.button[aria-expanded='true']:not([aria-current='page']) {
  background: color-mix(in srgb, var(--token-accent) 22%, transparent);
}

/* Sub-bar buttons mirror the main bar's pill style but a touch larger
 * so direct-jump targets are easier to tap mid-meeting. */
.subButton {
  min-height: 38px;
  padding: 6px 16px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--token-text-primary);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.subButton:hover {
  background: rgba(255, 255, 255, 0.4);
}

.subButton[aria-current='page'] {
  background: var(--token-accent);
  color: #fefdf6;
  box-shadow:
    0 6px 14px -6px rgba(190, 110, 35, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
}
